import {
  type FormattedPriceSettings,
  type ShowBundleWithContent,
  type TvShowBundleInfo,
} from '@setplex/tria-api'
import type { TFunction } from 'i18next'
import { type AdjustPurchaseableFn } from '~/features/payment/sse-purchases'
import type { ShowWithPrice } from '~/shared/interfaces'
import {
  extendMediaItemByLabelPrice,
  getLabelPriceForPurchasedItem,
} from '~/shared/tools'
import type { UiShowBundleWithContent, UiTvShowBundleInfo } from './index.h'

const getLowestPriceFromPriceSettings = (
  priceSettings: FormattedPriceSettings[] | null
): FormattedPriceSettings | null => {
  if (!priceSettings) return null

  return priceSettings.reduce((prev, min) =>
    Number(prev.price) < Number(min.price) ? prev : min
  )
}

export const formatBundleWithContent = (
  bundle: ShowBundleWithContent | null,
  adjustShow: AdjustPurchaseableFn,
  adjustSet: AdjustPurchaseableFn,
  t: TFunction
): UiShowBundleWithContent | null => {
  if (!bundle) return null

  const adjustedBundle = adjustSet(bundle)
  const { priceSettings, purchaseInfo, shows } = adjustedBundle

  const lowestPrice = getLowestPriceFromPriceSettings(priceSettings)
  const options = {
    price: lowestPrice?.price,
    currency: lowestPrice?.currency,
  }

  const rentedDuration = getLabelPriceForPurchasedItem({ purchaseInfo })
  const labelRented = rentedDuration
    ? t('rented_to', {
        duration: rentedDuration,
      })
    : ''

  const showsFormatted = adjustedBundle.purchaseInfo
    ? shows
    : shows.map(
        (show) =>
          extendMediaItemByLabelPrice(adjustShow(show), true) as ShowWithPrice
      )

  return {
    ...adjustedBundle,
    shows: showsFormatted,
    priceText: t('watch_all_for', options),
    labelRented,
  }
}

export const formatBundleRowWithContent =
  (t: TFunction, adjust: AdjustPurchaseableFn) =>
  (bundle: TvShowBundleInfo): UiTvShowBundleInfo => {
    bundle = adjust(bundle)
    const { labelPrice, rented } = extendMediaItemByLabelPrice(bundle)
    return {
      ...bundle,
      quantityText: t('series_amount', {
        amount: bundle.contentQuantity,
      }),
      labelPrice,
      rented,
      link: `/content/series/bundles/${bundle.id}`,
    }
  }
